import React from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
export default function Hero() {
  const {
    lang
  } = useLanguage();
  const eyebrow = pick(lang, {
    en: "STAINLESS STEEL INDUSTRIAL FILTRATION",
    zh: "不锈钢工业过滤与分离",
    es: "FILTRACIÓN INDUSTRIAL EN ACERO INOXIDABLE",
    de: "INDUSTRIELLE EDELSTAHLFILTRATION",
    fr: "FILTRATION INDUSTRIELLE EN ACIER INOXYDABLE",
    pt: "FILTRAÇÃO INDUSTRIAL EM AÇO INOXIDÁVEL"
  });
  const lead = pick(lang, {
    en: "Precision woven wire mesh, sintered laminates, filter components and wedge wire screens built for critical process conditions.",
    zh: "面向严苛工业工况，提供精密编织丝网、多层烧结网板、加工滤芯组件与全焊接楔形丝条缝筛。",
    es: "Mallas tejidas de precisión, laminados sinterizados, componentes de filtración y tamices de alambre en cuña para procesos críticos.",
    de: "Präzisions-Drahtgewebe, Sintermetall-Laminate, Filterkomponenten und Spaltsiebe für anspruchsvolle Prozessbedingungen.",
    fr: "Toiles métalliques de précision, stratifiés frittés, composants filtrants et crépines à fil profilé pour procédés exigeants.",
    pt: "Malhas tecidas de precisão, laminados sinterizados, componentes de filtração e telas de arame em cunha para processos críticos."
  });
  const ctaPrimary = pick(lang, {
    en: "Explore Products",
    zh: "浏览产品",
    es: "Explorar productos",
    de: "Produkte entdecken",
    fr: "Découvrir les produits",
    pt: "Explorar produtos"
  });
  const ctaSecondary = COMMON.requestQuote[lang] ?? COMMON.requestQuote.en;
  const technicalCaption = pick(lang, {
    en: "Precision Stainless Steel Filter Mesh — Uniform Pore Geometry",
    zh: "精密不锈钢过滤网微观结构 — 均匀孔隙与高机械强度",
    es: "Malla filtrante de acero inoxidable de precisión — Geometría de poro uniforme",
    de: "Präzisions-Edelstahlfiltergewebe — Gleichmäßige Porengeometrie",
    fr: "Toile filtrante de précision en acier inoxydable — Géométrie des pores uniforme",
    pt: "Malha filtrante de precisão em aço inoxidável — Geometria de poro uniforme"
  });
  const imageAlt = pick(lang, {
    en: "Macro view of precision stainless steel woven wire mesh for industrial filtration",
    zh: "工业过滤用精密不锈钢编织丝网微距特写",
    es: "Vista macro de malla de alambre tejida de precisión en acero inoxidable para filtración industrial",
    de: "Makroansicht von präzisem Edelstahl-Drahtgewebe für die industrielle Filtration",
    fr: "Vue macro d'une toile métallique tissée de précision en acier inoxydable pour la filtration industrielle",
    pt: "Visão macro de malha de arame tecida de precisão em aço inoxidável para filtração industrial"
  });
  const renderHeadline = () => {
    switch (lang) {
      case "zh":
        return <>
            工业<span className="text-accent">过滤与分离</span>产品
          </>;
      case "es":
        return <>
            Productos industriales de{" "}
            <span className="text-accent">filtración y separación</span>
          </>;
      case "de":
        return <>
            Industrielle Produkte für{" "}
            <span className="text-accent">Filtration &amp; Trennung</span>
          </>;
      case "fr":
        return <>
            Produits industriels de{" "}
            <span className="text-accent">filtration et séparation</span>
          </>;
      case "pt":
        return <>
            Produtos industriais de{" "}
            <span className="text-accent">filtração e separação</span>
          </>;
      case "en":
      default:
        return <>
            Industrial <span className="text-accent">Filtration &amp; Separation</span> Products
          </>;
    }
  };
  return <section id="hero" data-nav="dark" className="relative overflow-hidden border-b border-surface-dark-border bg-surface-dark text-surface-dark-foreground">
      <div className="mf-container relative mf-hero lg:mf-section">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Left Column: Technical Narrative & Actions */}
          <Reveal className="flex flex-col items-start lg:col-span-6">
            <span className="text-xs font-mono uppercase tracking-widest text-surface-dark-subtle mb-4">
              {eyebrow}
            </span>

            <h1 className="font-default mf-h1 text-surface-dark-foreground">
              {renderHeadline()}
            </h1>

            <p className="mt-4 max-w-xl text-base leading-relaxed text-surface-dark-muted sm:text-lg">
              {lead}
            </p>

            <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="lg" className="mf-btn-gold rounded-md px-6 text-sm font-semibold tracking-wide transition-[color,background-color,border-color] duration-200">
                <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>{ctaSecondary}</span>
                  <ArrowRight className="size-4 shrink-0 mf-arrow-nudge" aria-hidden="true" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md border-surface-dark-border bg-transparent px-6 text-sm font-medium text-surface-dark-foreground transition-[color,background-color,border-color] duration-200 hover:bg-surface-dark-elevated hover:text-surface-dark-foreground">
                <Link to="/ProductsPage" className="inline-flex items-center justify-center gap-2">
                  <span>{ctaPrimary}</span>
                  <ChevronRight className="size-4 text-surface-dark-subtle shrink-0 mf-arrow-nudge" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Macro Precision Media Display */}
          <Reveal delay={100} className="relative flex flex-col w-full lg:col-span-6">
            <div className="relative aspect-[4/3] min-h-[280px] sm:min-h-[340px] lg:min-h-[420px] w-full overflow-hidden rounded-lg bg-surface-dark-elevated mf-img-zoom">
              <img loading="eager" fetchPriority="high" decoding="async" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/precision_stainless_filter_mesh.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/precision_stainless_filter_mesh.png-wvc-sizes" alt={imageAlt} className="h-full w-full object-cover object-center" src="https://mutualfilter.com/wp-content/uploads/2026/09/01-woven-wire-cloths-and-meshes.webp" />
            </div>

            <div className="mt-2.5 flex items-center justify-between px-1 text-xs sm:text-sm text-surface-dark-subtle">
              <p className="truncate font-mono">{technicalCaption}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}